import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { BRAND } from '@/config/brand';

interface SidebarItem {
  label: string;
  path: string;
  icon: string;
  adminOnly?: boolean;
}

const sidebarItems: SidebarItem[] = [
  { label: 'Ventas', path: '/ventas', icon: 'ri-shopping-cart-2-line' },
  { label: 'Productos', path: '/productos', icon: 'ri-box-3-line' },
  { label: 'Valuación de inventario CPP', path: '/movimientos-inventario', icon: 'ri-scales-3-line', adminOnly: true },
  { label: 'Reportes', path: '/reportes', icon: 'ri-bar-chart-2-line' },
  { label: 'Historial Ventas', path: '/historial-ventas', icon: 'ri-file-list-3-line' },
  { label: 'Usuarios', path: '/usuarios', icon: 'ri-team-line', adminOnly: true },
  { label: 'Configuración', path: '/configuracion', icon: 'ri-settings-3-line', adminOnly: true },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    await logout();
    setShowLogoutConfirm(false);
    navigate('/login');
  };

  const filteredItems = sidebarItems.filter(
    (item) => !item.adminOnly || isSuperAdmin
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#F4F6F8]">
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-[#1F2937] text-white transition-all duration-300 flex flex-col hidden lg:flex`}
      >
        <div className="flex items-center h-16 px-3 border-b border-gray-700">
          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img src={BRAND.logoUrl} alt={BRAND.name} className="w-full h-full object-contain" />
          </div>
          {sidebarOpen && (
            <div className="ml-3 min-w-0">
              <h1 className="text-sm font-bold whitespace-nowrap">{BRAND.shortName}</h1>
              <p className="text-[10px] text-gray-400 whitespace-nowrap">Ltda.</p>
            </div>
          )}
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {filteredItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center mx-2 mb-1 px-3 py-2.5 rounded-lg whitespace-nowrap ${
                  isActive
                    ? 'bg-[#2563EB] text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                {sidebarOpen && <span className="ml-3 text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-700">
          <button
            onClick={handleLogoutClick}
            className="flex items-center w-full px-3 py-2.5 rounded-lg text-gray-300 hover:text-white hover:bg-red-500/20 whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line text-lg w-5 h-5 flex items-center justify-center"></i>
            {sidebarOpen && <span className="ml-3 text-sm">Cerrar Sesión</span>}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#1F2937] text-white flex flex-col">
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-700">
              <div className="flex items-center">
                <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                  <img src={BRAND.logoUrl} alt={BRAND.name} className="w-full h-full object-contain" />
                </div>
                <div className="ml-3">
                  <h1 className="text-sm font-bold">{BRAND.shortName}</h1>
                  <p className="text-[10px] text-gray-400">Ltda.</p>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-gray-400 hover:text-white">
                <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
            <nav className="flex-1 py-4 overflow-y-auto">
              {filteredItems.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center mx-2 mb-1 px-3 py-2.5 rounded-lg ${
                      isActive ? 'bg-[#2563EB] text-white' : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                    <span className="ml-3 text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="p-3 border-t border-gray-700">
              <button
                onClick={handleLogoutClick}
                className="flex items-center w-full px-3 py-2.5 rounded-lg text-gray-300 hover:text-white hover:bg-red-500/20"
              >
                <i className="ri-logout-box-r-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="ml-3 text-sm">Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white flex items-center justify-between px-4 md:px-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <button
              className="lg:hidden mr-3 text-gray-600 hover:text-gray-900"
              onClick={() => setMobileMenuOpen(true)}
            >
              <i className="ri-menu-line text-2xl w-6 h-6 flex items-center justify-center"></i>
            </button>
            <button
              className="hidden lg:flex text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <i className={`${sidebarOpen ? 'ri-sidebar-fold-line' : 'ri-sidebar-unfold-line'} text-xl w-5 h-5 flex items-center justify-center`}></i>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-600 bg-[#F4F6F8] px-3 py-1.5 rounded-lg">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              <span className="whitespace-nowrap">
                {new Date().toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#2563EB] rounded-full flex items-center justify-center text-white text-xs font-semibold">
                {user?.nombre?.charAt(0) || 'U'}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {user?.nombre || 'Usuario'}
                </p>
                <p className="text-xs text-gray-400 whitespace-nowrap capitalize">
                  {user?.rol === 'super_admin' ? 'Super Admin' : 'Usuario'}
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>

      <ConfirmDialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogoutConfirm}
        title="¿Cerrar sesión?"
        message="Tu sesión actual se cerrará y deberás volver a iniciar sesión."
        confirmText="Sí, salir"
        cancelText="Cancelar"
        confirmColor="primary"
        icon="ri-logout-box-r-line"
      />
    </div>
  );
}